import { LogLevels, Room, Track } from "twilio-video";

import { useCallback, useRef, useState } from "react";

type MediaStreamTrackPublishOptions = {
  name?: string;
  priority: Track.Priority;
  logLevel: LogLevels;
};

export const useScreenShareToggle = (
  room: Room | null,
  onError: (error: Error) => void
) => {
  const [isSharing, setIsSharing] = useState(false);
  const stopScreenShareRef = useRef<() => void>(null!);

  const shareScreen = useCallback(() => {
    navigator.mediaDevices
      .getDisplayMedia({
        audio: false,
        video: true,
      })
      .then((stream) => {
        const track = stream.getTracks()[0];

        room!.localParticipant
          .publishTrack(track, {
            name: "screen",
            priority: "low",
          } as MediaStreamTrackPublishOptions)
          .then((trackPublication) => {
            stopScreenShareRef.current = () => {
              room!.localParticipant.unpublishTrack(track);
              room!.localParticipant.emit("trackUnpublished", trackPublication);
              track.stop();
              setIsSharing(false);
            };

            track.onended = stopScreenShareRef.current;
            setIsSharing(true);
          })
          .catch(onError);
      })
      .catch((error) => {
        // Don't display an error if the user closes the screen share dialog
        if (
          error.message === "Permission denied by system" ||
          (error.name !== "AbortError" && error.name !== "NotAllowedError")
        ) {
          console.error(error);
          onError(error);
        }
      });
  }, [room, onError]);

  const toggleScreenShare = useCallback(() => {
    if (room) {
      !isSharing ? shareScreen() : stopScreenShareRef.current();
    }
  }, [isSharing, shareScreen, room]);

  return [isSharing, toggleScreenShare] as const;
};
